import React from "react";
import { PlayerBadges, hasPlayerImage } from "../services/BadgeService";
import { useRealPhotoIds } from "../services/RealPhotoService";
import PlayerImage from "./PlayerImage";

type PlayerAvatarProps = {
    steamId: string;
    /** Used for the monogram when there is no photo. */
    name?: string;
    size?: number;
    /** Border colour, e.g. to ring the leader of a stat. Omit for no border. */
    ring?: string;
    badges?: PlayerBadges | null;
};

/**
 * A circular player avatar that shows a photo only when a real one exists.
 *
 * The league CDN returns a black silhouette placeholder for roughly half of players, which looks
 * broken repeated down a list, so anything without a genuine photo gets an initial monogram
 * instead. Availability comes from {@link useRealPhotoIds} (one cached list per page), and a custom
 * upload always wins — that is what {@link PlayerImage} already resolves.
 *
 * The photos are head-and-torso cut-outs, so a circular crop is anchored to the top: centring it
 * would frame the chest and cut the face.
 */
const PlayerAvatar = ({ steamId, name, size = 32, ring, badges }: PlayerAvatarProps) => {
    const realPhotoIds = useRealPhotoIds();

    const hasCustomUpload = badges ? hasPlayerImage(steamId, badges) : false;
    const hasPhoto = hasCustomUpload || (realPhotoIds?.has(steamId) ?? false);

    const shared: React.CSSProperties = {
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        boxSizing: 'border-box',
        border: ring ? `2px solid ${ring}` : undefined,
    };

    if (hasPhoto) {
        return (
            <span style={{ ...shared, display: 'inline-block', overflow: 'hidden', background: '#262626' }}>
                <PlayerImage
                    id={steamId}
                    width={size * 2}
                    badges={badges ?? undefined}
                    imgStyle={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'top center',
                        display: 'block',
                    }}
                />
            </span>
        );
    }

    return (
        <span
            style={{
                ...shared,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.06)',
                border: ring ? `2px solid ${ring}` : '1px solid rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.4)',
                fontSize: Math.max(10, Math.round(size * 0.4)),
                fontWeight: 700,
                lineHeight: 1,
                userSelect: 'none',
            }}
            aria-hidden="true"
        >
            {(name || '?').charAt(0).toUpperCase()}
        </span>
    );
};

export default PlayerAvatar;
